import { Link } from "react-router-dom";
import { Flag as Whistle, ArrowRight, CheckCircle2, Mail, Phone, Users, Heart, GraduationCap, Shield } from "lucide-react";

export default function Recrutement() {
  return (
    <div data-testid="recrutement-page">
      {/* HERO */}
      <section className="relative bg-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/40 via-[#0F172A]/80 to-[#0F172A]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 bg-[#D97706] text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5">
            <Shield size={14} /> Rejoignez le staff
          </div>
          <h1 className="font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl" style={{ fontFamily: "Outfit" }}>
            Nous recrutons des <span className="text-[#D97706]">arbitres</span>.
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed text-lg">
            Vous aimez le foot et vous voulez vivre le match autrement ? Le Villeurbanne United FC
            recherche des <strong className="text-white">arbitres de club</strong> pour accompagner
            nos équipes jeunes et seniors. <strong className="text-white">Débutants bienvenus</strong>,
            formation et équipement pris en charge.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="mailto:presidencevufc@yahoo.com?subject=Candidature%20arbitre%20VUFC"
              data-testid="hero-cta-email"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#D97706] hover:bg-[#B45309] text-white font-semibold rounded-md transition-colors"
            >
              <Mail size={16} /> Postuler par email
            </a>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold rounded-md transition-colors border border-white/20"
            >
              Formulaire de contact <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* POURQUOI DEVENIR ARBITRE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Pourquoi nous rejoindre ?</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Devenir arbitre au VUFC
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pourquoi-grid">
          {[
            { icon: GraduationCap, title: "Formation prise en charge", desc: "Le club finance votre formation d'arbitre officiel FFF et vous accompagne durant toute la saison." },
            { icon: Heart, title: "Indemnités à chaque match", desc: "Les arbitres de club sont indemnisés pour chaque rencontre officielle qu'ils dirigent." },
            { icon: Users, title: "Une équipe soudée", desc: "Vous rejoignez une famille de passionnés : coachs, dirigeants, parents… tout le club est derrière vous." },
            { icon: Shield, title: "Équipement fourni", desc: "Tenue d'arbitrage complète (maillot, short, chaussettes, sifflet, cartons) offerte par le club." },
            { icon: Whistle, title: "Débutants acceptés", desc: "Aucune expérience requise : votre motivation et votre amour du jeu suffisent pour commencer." },
            { icon: CheckCircle2, title: "Horaires souples", desc: "Vous choisissez les matchs que vous arbitrez selon vos disponibilités (week-end principalement)." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="group rounded-lg border border-[#E2E8F0] bg-white p-6 hover:border-[#D97706] hover:-translate-y-1 transition-all duration-200" data-testid={`pourquoi-${i}`}>
                <div className="w-11 h-11 rounded-md bg-[#D97706]/10 text-[#D97706] group-hover:bg-[#D97706] group-hover:text-white flex items-center justify-center transition-colors">
                  <Icon size={20} />
                </div>
                <div className="mt-4 font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{c.title}</div>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* QUI PEUT POSTULER */}
      <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Qui peut postuler ?</span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
                Profil recherché
              </h2>
              <p className="mt-4 text-[#475569] leading-relaxed">
                Nous accueillons tous les profils, du jeune passionné au parent qui veut s'investir autrement
                dans la vie du club. Pas besoin d'avoir déjà arbitré — l'important c'est la motivation !
              </p>
              <ul className="mt-6 space-y-3 text-sm text-[#475569]" data-testid="profil-list">
                {[
                  "Être âgé de 15 ans minimum",
                  "Avoir une bonne condition physique",
                  "Être disponible le week-end (samedi ou dimanche)",
                  "Aimer le foot et le fair-play",
                  "Rigueur, impartialité, calme sous pression",
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border-2 border-[#D97706] bg-gradient-to-br from-[#FEF3C7]/40 to-white p-7">
              <div className="w-14 h-14 rounded-full bg-[#D97706] text-white flex items-center justify-center mb-5">
                <Whistle size={24} />
              </div>
              <h3 className="text-xl font-bold text-[#92400E]" style={{ fontFamily: "Outfit" }}>Comment candidater ?</h3>
              <p className="mt-3 text-sm text-[#78350F] leading-relaxed">
                Rien de plus simple ! Envoyez-nous un email ou passez-nous un coup de fil : le Président
                vous rappelle rapidement pour vous expliquer la suite (rendez-vous, formation, premier match).
              </p>
              <div className="mt-6 space-y-3">
                <a
                  href="mailto:presidencevufc@yahoo.com?subject=Candidature%20arbitre%20VUFC"
                  data-testid="cta-email"
                  className="flex items-center justify-between gap-3 p-3.5 rounded-md bg-white border border-[#E2E8F0] hover:border-[#D97706] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-[#D97706]" />
                    <div>
                      <div className="text-xs text-[#475569]">Email</div>
                      <div className="font-semibold text-[#0F172A] text-sm break-all">presidencevufc@yahoo.com</div>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-[#D97706] shrink-0" />
                </a>
                <a
                  href="tel:+33632475174"
                  data-testid="cta-phone"
                  className="flex items-center justify-between gap-3 p-3.5 rounded-md bg-white border border-[#E2E8F0] hover:border-[#D97706] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-[#D97706]" />
                    <div>
                      <div className="text-xs text-[#475569]">Téléphone (Président)</div>
                      <div className="font-semibold text-[#0F172A] text-sm">06 32 47 51 74</div>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-[#D97706] shrink-0" />
                </a>
                <Link
                  to="/contact"
                  data-testid="cta-form"
                  className="flex items-center justify-between gap-3 p-3.5 rounded-md bg-[#1E3A8A] text-white hover:bg-[#1E40AF] transition-colors"
                >
                  <div className="font-semibold text-sm">Ou utilisez le formulaire de contact</div>
                  <ArrowRight size={14} className="shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUTRES BÉNÉVOLAT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="rounded-lg border border-[#E2E8F0] bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white p-7 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="autres-roles">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Autres façons de nous aider</div>
            <h3 className="mt-2 text-2xl lg:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Entraîneur, accompagnateur, bénévole événementiel</h3>
            <p className="mt-3 text-slate-200 max-w-2xl">
              L'arbitrage ne vous attire pas ? Nous recrutons aussi des <strong className="text-white">éducateurs</strong>, des
              <strong className="text-white"> accompagnateurs</strong> pour les déplacements d'équipes, et des <strong className="text-white">bénévoles</strong>
              pour les tournois, la buvette ou les événements du club.
            </p>
          </div>
          <Link
            to="/faq"
            data-testid="cta-faq"
            className="px-5 py-3 rounded-md bg-white text-[#1E3A8A] font-semibold hover:bg-[#D97706] hover:text-white transition-colors flex items-center gap-2 shrink-0"
          >
            Lire la FAQ Vie du club <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
